import React from 'react';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-900 text-white">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center">
            <img 
              src="/clawbrand_logo_color.png" 
              alt="ClawBrand AI" 
              className="h-16 mx-auto mb-6"
            />
            <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
            <p className="text-gray-300">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 mb-6">
              By accessing or using BrandScapes AI, you agree to be bound by these Terms of Service and all 
              applicable laws and regulations. If you do not agree with any of these terms, you are prohibited 
              from using or accessing this service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Use License</h2>
            <p className="text-gray-700 mb-4">
              Upon purchasing a subscription to BrandScapes AI, we grant you a non-exclusive, non-transferable 
              license to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Access and use the BrandScapes AI platform for your personal or business purposes</li>
              <li>Create unlimited mockups using our AI-powered tools</li>
              <li>Download and use the mockups you create for commercial purposes</li>
              <li>Upload your own images and branding materials to create custom mockups</li>
            </ul>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-800 mb-2">Restrictions</h3>
              <p className="text-gray-700 mb-2">You may NOT:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Share your account credentials with others</li>
                <li>Resell or redistribute the BrandScapes AI service</li>
                <li>Reverse engineer or attempt to extract the source code</li>
                <li>Use the service for any illegal or unauthorized purpose</li>
                <li>Violate any laws in your jurisdiction</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Account Terms</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account and password</li>
              <li>You must be at least 18 years old to use this service</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>You must notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscription and Billing</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Lifetime Access</h3>
            <p className="text-gray-700 mb-4">
              BrandScapes AI offers lifetime access plans. By purchasing a lifetime plan:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>You pay a one-time fee for unlimited access to the platform</li>
              <li>No recurring charges or hidden fees</li>
              <li>Access to all current features and reasonable future updates</li>
              <li>Subject to our fair use policy</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Payment Terms</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>All payments are processed securely through our payment provider</li>
              <li>Prices are in USD unless otherwise stated</li>
              <li>We accept major credit cards and other payment methods as displayed</li>
              <li>All sales are subject to our refund policy</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Content</h3>
            <p className="text-gray-700 mb-4">
              You retain all rights to the images, logos, and content you upload to BrandScapes AI. By using our service:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>You grant us a license to process your content solely for providing the service</li>
              <li>You confirm you have the right to use any content you upload</li>
              <li>You own all mockups you create using our platform</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Our Content</h3>
            <p className="text-gray-700 mb-6">
              The BrandScapes AI platform, including its software, design, and templates, is protected by copyright 
              and other intellectual property laws. You may not copy, modify, or distribute our proprietary content 
              without permission.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Fair Use Policy</h2>
            <p className="text-gray-700 mb-4">
              While we offer unlimited mockup creation, we maintain a fair use policy to ensure quality service for all users:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Reasonable usage for personal or business purposes is unlimited</li>
              <li>Automated or bot usage is prohibited</li>
              <li>Excessive usage that impacts service performance may be limited</li>
              <li>Commercial resale of the service itself is not permitted</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Privacy and Data Protection</h2>
            <p className="text-gray-700 mb-6">
              Your use of BrandScapes AI is also governed by our Privacy Policy. We are committed to protecting 
              your personal information and maintaining the confidentiality of your data. Please review our 
              Privacy Policy for detailed information about how we collect, use, and protect your data.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimers and Limitations</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Service Disclaimer</h3>
              <p className="text-gray-700">
                BrandScapes AI is provided "as is" and "as available" without any warranties of any kind, either 
                express or implied. We do not warrant that the service will be uninterrupted, error-free, or 
                completely secure.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Limitation of Liability</h3>
            <p className="text-gray-700 mb-6">
              To the maximum extent permitted by law, BrandScapes AI shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages resulting from your use or inability 
              to use the service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Indemnification</h2>
            <p className="text-gray-700 mb-6">
              You agree to indemnify and hold harmless BrandScapes AI, its affiliates, and their respective 
              officers, directors, employees, and agents from any claims, damages, losses, liabilities, and 
              expenses arising out of your use of the service or violation of these terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to terminate or suspend your account immediately, without prior notice or 
              liability, for any reason, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Breach of these Terms of Service</li>
              <li>Fraudulent or illegal activity</li>
              <li>Violation of our fair use policy</li>
              <li>At your request to close your account</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-700 mb-6">
              We reserve the right to modify or replace these Terms of Service at any time. If a revision is 
              material, we will provide at least 30 days notice prior to any new terms taking effect. Your 
              continued use of the service after changes constitutes acceptance of the new terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-700 mb-6">
              These Terms of Service are governed by and construed in accordance with the laws of the United 
              States, without regard to its conflict of law provisions. Any disputes arising from these terms 
              shall be resolved through binding arbitration.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
              <p className="text-gray-700">
                <strong>Email:</strong> support@brandscapesai.com<br />
                <strong>Website:</strong> Contact form on our website
              </p>
            </div>

            <div className="mt-12 p-6 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                By using BrandScapes AI, you acknowledge that you have read, understood, and agree to be bound 
                by these Terms of Service. These terms are effective as of {new Date().toLocaleDateString()}.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 mt-16">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-sm">
              © {new Date().getFullYear()} BrandScapes AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
